import type { IAnvändare } from 'src/types/nya/common';
import type { NavGroupProps, NavSectionProps } from 'src/components/nav-section';

import { paths } from 'src/routes/paths';

import type { NavData } from './nav-permissions';
import { ICONS } from './nav-icons';
import {
  BEHÖRIGHET,
  byggMenyKatalog,
  byggMenyTräd,
  filtreraNavigering,
  härledBehörigheter,
} from './nav-permissions';

export type { Behörighet, MenyKatalogPost, MenyKatalogNod } from './nav-permissions';
export { ärMenySynlig, ROLLER_FÖR_MENYINSTÄLLNINGAR } from './nav-permissions';

// ----------------------------------------------------------------------

/**
 * Sidebarens innehåll.
 *
 * Strukturen är lagrad efter vem posten är till för, inte efter teknisk domän:
 * en boende möter start + "Mitt boende" + "Föreningen" och inget mer, medan
 * styrelse, ekonomiansvariga och administratörer får sina sektioner ovanpå.
 * Håll toppnivån kort — en roll bör inte se fler än sju poster per sektion.
 *
 * Behörigheter:
 * - Löv ska alltid ange `allowedRoles` (annars är de osynliga för alla).
 * - Föräldrar utan `allowedRoles` ärver unionen av barnens, se
 *   {@link härledBehörigheter}. Sätt `allowedRoles` på en förälder bara när den
 *   ska vara en hård grind som barnen inte kan öppna.
 * - `menyNyckel` på en toppnivåpost gör den av/på-bar per organisation och
 *   plockas automatiskt upp av {@link MENY_KATALOG}.
 */
const NAVIGERING: NavData = [
  /**
   * Start — det alla ser först, oavsett roll.
   */
  {
    items: [
      {
        title: 'Hem',
        path: paths.dashboard.root,
        icon: ICONS.hem,
        allowedRoles: BEHÖRIGHET.ALLA,
      },
      {
        // Introduktionssidan, dit onboardingen lämnar av en ny organisation. Egen nyckel så att
        // en förening som varit igång i tre år kan stänga av den — men inte `standardDold`: en
        // nyskapad organisation har inga sparade menyinställningar alls, och `ärMenySynlig`
        // hade då dolt sidan för exakt den som just skickades dit.
        title: 'Kom igång',
        path: paths.dashboard.komIgång,
        icon: ICONS.komIgång,
        menyNyckel: 'kom-igang',
        allowedRoles: BEHÖRIGHET.ALLA,
      },
      {
        // Säljytan. Märkt `alla` med flit — vem som helst ska kunna se appen med
        // en påhittad förening — men med egen nyckel så en förening som redan är
        // igång kan stänga av den under Organisation → Inställningar.
        title: 'Demo',
        path: paths.dashboard.demo,
        icon: ICONS.demo,
        menyNyckel: 'demo',
        allowedRoles: BEHÖRIGHET.ALLA,
      },
      {
        title: 'Meddelanden',
        path: paths.dashboard.meddelanden.root,
        icon: ICONS.meddelanden,
        menyNyckel: 'meddelanden',
        children: [
          {
            title: 'Chattar',
            path: paths.dashboard.meddelanden.root,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            // Föreningens ämneskanaler. Kräver en förening — som privatperson
            // finns det inget att hämta.
            title: 'Kanaler',
            path: paths.dashboard.boende.kanaler,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            // Egen allowedRoles bredare än barnens med flit: filtreras alla barn bort blir
            // posten en ren länk till listan (se filtreraPost i nav-permissions.ts) — den
            // boende ser "Utskick" som en direkt länk, styrelsen ser hela undermenyn.
            title: 'Utskick',
            path: paths.dashboard.utskick.root,
            menyNyckel: 'utskick',
            allowedRoles: BEHÖRIGHET.BOENDE,
            children: [
              {
                title: 'Skapa',
                path: paths.dashboard.utskick.skapa,
                allowedRoles: BEHÖRIGHET.STYRELSE,
              },
              {
                title: 'Skapa (ny)',
                path: paths.dashboard.utskick.skapaV2,
                allowedRoles: BEHÖRIGHET.STYRELSE,
              },
              {
                title: 'Arkiv',
                path: paths.dashboard.utskick.arkiv,
                allowedRoles: BEHÖRIGHET.STYRELSE,
              },
              {
                title: 'Inkorg',
                path: paths.dashboard.inkorg,
                allowedRoles: BEHÖRIGHET.STYRELSE,
              },
            ],
          },
        ],
      },
      {
        title: 'Ärenden',
        path: paths.dashboard.arende.root,
        icon: ICONS.ärenden,
        menyNyckel: 'arenden',
        children: [
          {
            title: 'Mina ärenden',
            path: paths.dashboard.arende.root,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Anmäl nytt ärende',
            path: paths.dashboard.arende.nytt,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Från boende',
            path: paths.dashboard.arende.boende,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Styrelsens ärenden',
            path: paths.dashboard.arende.styrelse,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
        ],
      },
    ],
  },

  /**
   * Min anställning — den anställdas egna sidor.
   *
   * Eget block och inte under Mitt boende: en förskolas personal är inte medlemmar i
   * kooperativet, och rollen ANSTÄLLD ger med flit inga boendebehörigheter. `standardDold` gör
   * att föreningar utan anställda inte ser blocket alls; den som har personal slår på det i
   * menyinställningarna.
   *
   * Posterna syns bara för den som har rollen, men rollen räcker inte för att se något: varje vy
   * kräver att `anställda.användar_id` pekar på kontot, och utan koppling svarar backenden 403
   * med en förklaring. Inbjudningslänken är delad per organisation, så rollen får aldrig vara det
   * som öppnar någons lönedata.
   */
  {
    subheader: 'Min anställning',
    items: [
      {
        title: 'Min anställning',
        path: paths.dashboard.minAnställning.tid,
        icon: ICONS.minAnställning,
        menyNyckel: 'min-anstallning',
        standardDold: true,
        children: [
          {
            title: 'Min tid',
            path: paths.dashboard.minAnställning.tid,
            allowedRoles: BEHÖRIGHET.ANSTÄLLD,
          },
          {
            title: 'Min frånvaro',
            path: paths.dashboard.minAnställning.frånvaro,
            allowedRoles: BEHÖRIGHET.ANSTÄLLD,
          },
        ],
      },
    ],
  },

  /**
   * Mina barn — vårdnadshavarens egen yta.
   *
   * Eget block och inte ett löv under Förskola: en förälder ska inte se verksamhetens vyer. Rollen
   * `vårdnadshavare` ger med flit inga boendebehörigheter — en förälder i ett kooperativ är inte
   * boende i en förening.
   *
   * **Rollen räcker inte för att se något.** `MinaBarnController` härleder barnen ur
   * hushållsnyckeln i medlemsregistret, så den som inte matchar ett hushåll med inskrivna barn får
   * en tom lista. Menyposten öppnar vyn, matchningen öppnar datat.
   */
  {
    subheader: 'Mina barn',
    items: [
      {
        title: 'Mina barn',
        path: paths.dashboard.minaBarn,
        icon: ICONS.minaBarn,
        menyNyckel: 'mina-barn',
        standardDold: true,
        allowedRoles: BEHÖRIGHET.VÅRDNADSHAVARE,
      },
    ],
  },

  /**
   * Förskolan — verksamhetens struktur, barnens tider, personalens placering och schemat.
   *
   * `standardDold` av samma skäl som "Min anställning": en bostadsrättsförening driver ingen
   * förskola och ska inte se blocket. Föräldrakooperativet slår på det, och demogeneratorn gör
   * det åt demo-organisationen.
   *
   * **Barnregistret ligger bakom ADMIN och inget annat.** Raderna bär barnens namn, och
   * `FörskolaController` kräver ORGANISATION_ADMIN på just de metoderna — en kassör som såg
   * menyposten hade bara fått 403. Avdelningar, Personal och Schema bär antal och
   * anställningsuppgifter, inte barnens namn, och får därför verksamhetens bredare behörighet.
   */
  {
    subheader: 'Förskola',
    items: [
      {
        title: 'Förskola',
        path: paths.dashboard.förskola.schema,
        icon: ICONS.förskola,
        menyNyckel: 'forskola',
        standardDold: true,
        children: [
          {
            title: 'Personal',
            path: paths.dashboard.förskola.personal,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Schema',
            path: paths.dashboard.förskola.schema,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Avdelningar',
            path: paths.dashboard.förskola.avdelningar,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Barnregister',
            path: paths.dashboard.förskola.barnregister,
            allowedRoles: BEHÖRIGHET.ADMIN,
          },
          {
            // EKONOMI och inte ADMIN: underlaget bär antal barn per åldersgrupp och inget enda
            // namn, så det delar verksamhetens behörighet med Schema och Avdelningar.
            // FörskolepengController har samma rolluppsättning.
            title: 'Förskolepeng',
            path: paths.dashboard.förskola.förskolepeng,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
        ],
      },
    ],
  },

  /**
   * Mitt boende — funktionerna en medlem faktiskt använder i vardagen.
   */
  {
    subheader: 'Mitt boende',
    items: [
      {
        // Mitt hem och Fastighet var två grenar med samma ämne — hemmet man bor i och fastigheten
        // man äger eller förvaltar. De slogs ihop hit; föreningens driftsytor ligger kvar som
        // styrelsebarn längre ned.
        //
        // ALLA på föräldern: Offerter, Mina fastigheter och Hantverkare vänder sig till den som
        // äger en fastighet, i första hand en privatperson utan roll. Föräldern måste släppa
        // igenom dem, annars stänger den hela grenen (se härledBehörigheter). Barnen bär sina
        // egna gränser — boende för hemmets ytor, styrelse för driften.
        //
        // menyNyckel 'fastighet': den gamla 'mitt-hem' migrerades in i den (Liquibase 230).
        title: 'Fastighet',
        path: paths.dashboard.fastighet.minaHem,
        icon: ICONS.fastighet,
        menyNyckel: 'fastighet',
        allowedRoles: BEHÖRIGHET.ALLA,
        children: [
          {
            title: 'Mitt hem',
            path: paths.dashboard.fastighet.minaHem,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Planlösning',
            path: paths.dashboard.fastighet.planlösning,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Planlösning V2',
            path: paths.dashboard.fastighet.planlösningV2,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Dokumentation',
            path: paths.dashboard.fastighet.dokumentation,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Kommunikationer',
            path: paths.dashboard.fastighet.kommunikationer,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Skapa',
            path: paths.dashboard.fastighet.skapa,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Foton',
            path: paths.dashboard.fastighet.foton,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Demo React 3 fiber',
            path: paths.dashboard.fastighet.demoReactThreeFiber,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Värme',
            path: paths.dashboard.fastighet.värme,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Förbrukning (IMD)',
            path: paths.dashboard.fastighet.imd,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'El',
            path: paths.dashboard.fastighet.el,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Värmepump',
            path: paths.dashboard.fastighet.värmepump,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Komponenter',
            path: paths.dashboard.fastighet.komponenter,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            // Läsning är öppen för boende: underhållsplanen är det som förklarar varför avgiften
            // ser ut som den gör, och en medlem som inte får se den kan inte bedöma sin förening.
            title: 'Underhållsplan',
            path: paths.dashboard.fastighet.underhållsplan,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            // Öppen för boende av samma skäl som underhållsplanen, och ett starkare: OVK-intyget
            // och energideklarationens sammanfattning SKA enligt lag anslås där de som bor i huset
            // ser dem. Om husets ventilation är godkänd är därför ingen styrelseinformation.
            // Protokollen, anmärkningarna och skyddsrummets beteckning är det — vyn visar dem bara
            // för styrelsen, och backend spärrar dem per endpoint.
            //
            // STYRELSE och REVISOR står med uttryckligen: `förvaltare` och `revisor` saknar
            // behörigheten 'boende' i ROLL_TILL_BEHÖRIGHETER, så BOENDE ensamt hade dolt sidan för
            // dem fast backend släpper in dem.
            title: 'Egenkontroll',
            path: paths.dashboard.fastighet.egenkontroll,
            allowedRoles: [...BEHÖRIGHET.BOENDE, ...BEHÖRIGHET.STYRELSE, ...BEHÖRIGHET.REVISOR],
          },
          {
            // Brandskydd: uppkopplade brandvarnare (LoRaWAN & M-Bus), gateways, larmloggar och SBA.
            // Öppen för boende (eget hems status och allmänt brandskydd) och styrelse/förvaltning.
            title: 'Brandskydd',
            path: paths.dashboard.fastighet.brandskydd,
            allowedRoles: [...BEHÖRIGHET.BOENDE, ...BEHÖRIGHET.STYRELSE, ...BEHÖRIGHET.REVISOR],
          },
          {
            // Engångsjobbet när en förening flyttar in: förvaltarens register läses ur en PDF och
            // granskas rad för rad innan det blir komponentposter.
            title: 'Importera register',
            path: paths.dashboard.fastighet.registerimport,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            // Där fönster, portar och balkonger räknas in. Scenen härleder dem annars ur måtten,
            // och det är en uppskattning — den här vyn är platsen för det någon faktiskt räknat.
            title: 'Husens mått',
            path: paths.dashboard.fastighet.byggnadsmått,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Vårt område (3D)',
            path: paths.dashboard.fastighet.område,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Våra byggnader (3D)',
            path: paths.dashboard.fastighet.byggnader.root,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Skapa 3D-modell',
            path: paths.dashboard.fastighet.byggnader.ny,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Ritningar (3D)',
            path: paths.dashboard.fastighet.ritning.root,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Ny ritning',
            path: paths.dashboard.fastighet.ritning.ny,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Offerter',
            path: paths.dashboard.fastighet.offerter.root,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Mina fastigheter',
            path: paths.dashboard.fastighet.minaFastigheter,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Energideklaration',
            path: paths.dashboard.fastighet.energideklaration,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            // Skatteverkets fastighetsdeklaration av småhus. ALLA av samma skäl som grenen i
            // övrigt: en villaägare utan roll i någon förening ska komma åt sitt eget hem.
            title: 'Deklarera småhus',
            path: paths.dashboard.fastighet.fastighetsdeklaration,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Hantverkare',
            path: paths.dashboard.fastighet.hantverkare,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
        ],
      },
      {
        // Digitala processer som Flinky för en genom, t.ex. köksrenoveringen. Vänder sig till
        // alla som äger sin bostad — också villaägare utan roll — så föräldern släpper igenom
        // ALLA. Handläggarkön är Flinky-personalens, och bär sin egen gräns.
        title: 'Projekt',
        path: paths.dashboard.projekt.root,
        icon: ICONS.projekt,
        menyNyckel: 'projekt',
        allowedRoles: BEHÖRIGHET.ALLA,
        children: [
          {
            title: 'Mina projekt',
            path: paths.dashboard.projekt.root,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            // Bygglovsguiden och dina ansökningar. Bygglov är ett slags projekt och startas
            // därifrån; sidan ligger kvar på sin gamla adress så att befintliga länkar fungerar.
            title: 'Bygglov',
            path: paths.dashboard.fastighet.bygglov,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Handläggarkö',
            path: paths.dashboard.projekt.handläggare,
            allowedRoles: BEHÖRIGHET.FLINKY_PERSONAL,
          },
        ],
      },
      {
        title: 'Utlåning',
        path: paths.dashboard.boende.utlåningspool.root,
        icon: ICONS.bokaLåna,
        menyNyckel: 'boka-lana',
        children: [
          {
            title: 'Mina bokningar',
            path: paths.dashboard.boende.utlåningspool.minaBokningar,
            menyNyckel: 'utlaningspool-mina',
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Inköpsförslag',
            path: paths.dashboard.boende.utlåningspool.Inköpsförslag,
            menyNyckel: 'utlaningspool-inkop',
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            // Importen når man från Utlåningspool-vyns egen Importera-knapp,
            // så den behöver ingen egen menypost.
            title: 'Admin',
            path: paths.dashboard.boende.utlåningspool.admin,
            menyNyckel: 'utlaningspool-admin',
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
        ],
      },
      {
        title: 'Tvättstuga',
        path: paths.dashboard.boende.bokaTvattstuga,
        icon: ICONS.bokaLåna,
        menyNyckel: 'boka-lana-tvattstuga',
        standardDold: true,
        allowedRoles: BEHÖRIGHET.BOENDE,
      },
      {
        // Allt som står parkerat eller rullar. Bröts ut ur "Boka & låna" — bilpool och
        // parkering hör ihop med varandra, inte med tvättstugan.
        title: 'Fordon',
        path: paths.dashboard.boende.garage,
        icon: ICONS.fordon,
        menyNyckel: 'fordon',
        children: [
          {
            title: 'Garage & parkering',
            path: paths.dashboard.boende.garage,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Bilpool',
            path: paths.dashboard.boende.bilpool,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Lådcykelpool',
            path: paths.dashboard.boende.lådcykelpool,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Släp',
            path: paths.dashboard.boende.släp,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            // Ingen bil som föreningen äger, men samma fråga för den boende: hur tar jag mig
            // dit. Egen menyNyckel så att en förening kan stänga av just samåkningen utan att
            // förlora bilpoolen.
            title: 'Samåkning',
            path: paths.dashboard.boende.samåkning.root,
            menyNyckel: 'samakning',
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
        ],
      },
      {
        title: 'Karta',
        path: paths.dashboard.karta.root,
        icon: ICONS.karta,
        menyNyckel: 'karta',
        children: [
          {
            title: 'Utforska',
            path: paths.dashboard.karta.root,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Mina platser',
            path: paths.dashboard.karta.platser,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Senaste',
            path: paths.dashboard.karta.senaste,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
        ],
      },
    ],
  },

  /**
   * Föreningen — det gemensamma. Öppet för medlemmar högst upp, styrelsens
   * arbetsytor under.
   */
  {
    subheader: 'Föreningen',
    items: [
      {
        title: 'Dokument',
        path: paths.dashboard.dokument.root,
        icon: ICONS.dokument,
        menyNyckel: 'dokument',
        allowedRoles: BEHÖRIGHET.ALLA,
      },
      {
        // Fakta om den förening som är vald just nu — stadgarna, grunduppgifterna,
        // vem som är vad och den senaste årsredovisningen.
        //
        // Kontots egna föreningsrelationer (vilka organisationer användaren
        // tillhör, registrera en ny, gå med i en befintlig) hör INTE hemma här:
        // sidomenyn är scopad till den valda organisationen och filtreras dessutom
        // på `menyNyckel`, så en förening som stänger av den här grenen skulle
        // annars ta bort användarens väg in i en *annan* förening. De ligger i
        // stället på kontoytorna — kontodrawern och organisationsväljaren, båda i
        // headern — och samlas på /organisation/mina.
        title: 'Om föreningen',
        path: paths.dashboard.organisation.grunddata,
        icon: ICONS.förening,
        menyNyckel: 'foreningen',
        children: [
          {
            // Alla medlemmar har rätt att läsa stadgarna — redigering är gated i
            // vyn och i backend till styrelse/admin.
            title: 'Stadgar',
            path: paths.dashboard.organisation.stadgar,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            // Grunduppgifterna ur årsredovisningen. Läsbara för alla medlemmar,
            // men bara styrelsen kan läsa in en ny årsredovisning — det är
            // gated i vyn och i backend.
            title: 'Grunduppgifter',
            path: paths.dashboard.organisation.grunddata,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            // Vem som är vad i föreningen är inget hemligt för dess medlemmar —
            // listan är öppen, men bara admin kan ändra i den.
            title: 'Medlemmar & kontakter',
            path: paths.dashboard.organisation.anvandare,
            allowedRoles: ['medlem', 'styrelseledamot', 'egenföretagare'],
          },
          {
            // Egen nyckel: länken går in i ekonomigrenen, så den ska kunna
            // parkeras utan att resten av "Om föreningen" följer med.
            title: 'Årsredovisning',
            path: paths.dashboard.ekonomi.arsredovisningar.senaste,
            menyNyckel: 'foreningen-arsredovisning',
            allowedRoles: BEHÖRIGHET.ALLA,
          },
        ],
      },
      {
        title: 'Styrelsen',
        path: paths.dashboard.styrelse.nastaMote,
        icon: ICONS.styrelse,
        menyNyckel: 'styrelse',
        allowedRoles: BEHÖRIGHET.STYRELSE,
        children: [
          {
            title: 'Nästa möte',
            path: paths.dashboard.styrelse.nastaMote,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Ledamoter',
            path: paths.dashboard.styrelse.ledamoter,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Arkiv',
            path: paths.dashboard.styrelse.arkiv.root,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Omröstning',
            path: paths.dashboard.styrelse.omröstning.root,
            menyNyckel: 'omrostning',
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
        ],
      },
      {
        title: 'Medlemsregister',
        // v2 är det aktuella registret (Hushåll + Person). Den äldre vyn på
        // `medlemmar.root` ligger kvar som rutt men länkas inte längre.
        path: paths.dashboard.medlemmar.v2,
        icon: ICONS.medlemmar,
        menyNyckel: 'medlemmar',
        allowedRoles: BEHÖRIGHET.STYRELSE,
        children: [
          {
            title: 'Register',
            path: paths.dashboard.medlemmar.v2,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Importera',
            path: paths.dashboard.medlemmar.importera,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            // Egen nyckel, och inte en del av `medlemmar`: en avgift blir till slut
            // en kundfaktura och drar därmed in räkenskapsår, bokföringskonto och
            // bank — hela den gren registret självt klarar sig utan.
            title: 'Avgifter',
            path: paths.dashboard.medlemmar.avgifter,
            menyNyckel: 'medlemmar-avgifter',
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Månadsavier',
            path: paths.dashboard.medlemmar.månadsfakturor,
            menyNyckel: 'medlemmar-avier',
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Användare & roller',
            path: paths.dashboard.organisation_users,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
        ],
      },
      {
        title: 'Aktiebok',
        path: paths.dashboard.aktiebok.root,
        icon: ICONS.medlemmar,
        menyNyckel: 'aktiebok',
        allowedRoles: [...BEHÖRIGHET.KONSULT, ...BEHÖRIGHET.STYRELSE],
      },
    ],
  },

  /**
   * Ekonomi — hela sektionen kräver behörigheten `ekonomi`, som styrelse,
   * kassör, ekonomiassistent, förvaltare och egenföretagare får via mappningen.
   */
  {
    subheader: 'Ekonomi',
    items: [
      {
        title: 'Översikt',
        path: paths.dashboard.ekonomi.root,
        icon: ICONS.ekonomi,
        menyNyckel: 'ekonomi-oversikt',
        allowedRoles: BEHÖRIGHET.EKONOMI,
      },
      {
        title: 'Bank',
        path: paths.dashboard.ekonomi.bank.transaktionskonto.root,
        icon: ICONS.ekonomi,
        menyNyckel: 'bank',
        children: [
          {
            title: 'Transaktionskonto',
            path: paths.dashboard.ekonomi.bank.transaktionskonto.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Bankkonton',
            path: paths.dashboard.ekonomi.bank.konton.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
        ],
      },
      {
        title: 'Bokföring',
        path: paths.dashboard.ekonomi.bokforing.root,
        icon: ICONS.bokföring,
        menyNyckel: 'bokforing',
        children: [
          {
            title: 'Bokför',
            path: paths.dashboard.ekonomi.bokforing.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Bokförda händelser',
            path: paths.dashboard.ekonomi.bokforing.händelser.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Verifikationer',
            path: paths.dashboard.ekonomi.bokforing.verifikationer.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Huvudbok',
            path: paths.dashboard.ekonomi.bokforing.huvudbok,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Grundbok',
            path: paths.dashboard.ekonomi.bokforing.grundbok,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Importera',
            path: paths.dashboard.ekonomi.bokforing.bokslut.importera,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Exportera',
            path: paths.dashboard.ekonomi.bokforing.exportera,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Aktieutdelning',
            path: paths.dashboard.aktieutdelning.root,
            menyNyckel: 'aktieutdelning',
            allowedRoles: BEHÖRIGHET.KONSULT,
          },
        ],
      },
      {
        title: 'Rapporter & bokslut',
        path: paths.dashboard.ekonomi.bokforing.resultaträkning,
        icon: ICONS.rapporter,
        menyNyckel: 'rapporter',
        children: [
          {
            title: 'Resultaträkning',
            path: paths.dashboard.ekonomi.bokforing.resultaträkning,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Balansräkning',
            path: paths.dashboard.ekonomi.bokforing.balansräkning,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Bokslut',
            path: paths.dashboard.ekonomi.bokforing.bokslut.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Årsredovisning',
            path: paths.dashboard.ekonomi.arsredovisningar.senaste,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Arkiv',
            path: paths.dashboard.ekonomi.arsredovisningar.arkiv,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Utkast för ny',
            path: paths.dashboard.ekonomi.arsredovisningar.utkast,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Sök hos Bolagsverket',
            path: paths.dashboard.ekonomi.arsredovisningar.sokBolagsverket,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
        ],
      },
      {
        title: 'Fakturor',
        path: paths.dashboard.ekonomi.fakturor.kundfakturor,
        icon: ICONS.fakturor,
        menyNyckel: 'fakturor',
        children: [
          {
            // STYRELSE och inte EKONOMI, till skillnad från sina syskon: BEHÖRIGHET.EKONOMI
            // släpper in kassör, ekonomiassistent, förvaltare och revisor via
            // ROLL_TILL_BEHÖRIGHETER, medan InkomnaFakturorController tillåter styrelse,
            // egenföretagare och admin. Det glappet finns redan på Kundfakturor — en kassör
            // ser menyposten och får 403 på varje anrop — och ska inte upprepas här.
            title: 'Inkomna fakturor',
            path: paths.dashboard.ekonomi.fakturor.inkomna,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Kundfakturor',
            path: paths.dashboard.ekonomi.fakturor.kundfakturor,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Leverantörsfakturor',
            path: paths.dashboard.ekonomi.bokforing.händelser.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            // Omdöpt från "Mottagna e-postfakturor": vyn filtrerar inte på fakturor utan visar
            // all inkommande post. Med "Inkomna fakturor" ovanför blev den gamla rubriken
            // direkt vilseledande.
            title: 'All inkommande e-post',
            path: paths.dashboard.ekonomi.mottagnaEmail,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'E-postinställningar',
            path: paths.dashboard.ekonomi.emailInställningar,
            allowedRoles: BEHÖRIGHET.ADMIN,
          },
        ],
      },
      {
        title: 'Lön',
        path: paths.dashboard.ekonomi.loner.root,
        icon: ICONS.lön,
        menyNyckel: 'lon',
        children: [
          {
            title: 'Lönekörningar',
            path: paths.dashboard.ekonomi.loner.körningar,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            // ADMIN och inte EKONOMI: registret innehåller personnummer och bankkonto, och
            // AnställdController är ORGANISATION_ADMIN-spärrad i sin helhet. Stämmer de två inte
            // ser en kassör menyposten och får 403 på varje anrop.
            title: 'Anställda',
            path: paths.dashboard.ekonomi.loner.anställda,
            allowedRoles: BEHÖRIGHET.ADMIN,
          },
          {
            // EKONOMI och inte ADMIN: den som matar in löneunderlag måste se vad som kommer in.
            // Besluten inne i vyn är snävare — TidOchFrånvaroController har styrelse- och
            // adminroller på metodnivå, och de väntande listorna ger 403 för övriga, vilket vyn
            // tål och hämtar i ett eget anrop.
            title: 'Tid och frånvaro',
            path: paths.dashboard.ekonomi.loner.tidOchFrånvaro,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Lönearter',
            path: paths.dashboard.ekonomi.loner.lönearter,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            // Det gamla AGD-flödet, omdöpt så det går att skilja från v2. "Lönekörningar" pekade
            // tidigare hit, vilket gjorde posten ovan omöjlig att hitta.
            title: 'Löner att bokföra',
            path: paths.dashboard.ekonomi.loner.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Ny lön',
            path: paths.dashboard.ekonomi.loner.ny,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
        ],
      },
      {
        title: 'Skatt',
        path: paths.dashboard.ekonomi.skatter.root,
        icon: ICONS.skattLöner,
        menyNyckel: 'skatt',
        children: [
          {
            title: 'Skattekonto',
            path: paths.dashboard.ekonomi.skatter.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Moms',
            path: paths.dashboard.ekonomi.moms.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Ny momsdeklaration',
            path: paths.dashboard.ekonomi.moms.ny,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Momsdeklarationer',
            path: paths.dashboard.ekonomi.moms.momsdeklarationer.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
        ],
      },
      {
        title: 'Försäkringar',
        path: paths.dashboard.ekonomi.forsakringar.root,
        icon: ICONS.försäkringar,
        menyNyckel: 'forsakringar',
        allowedRoles: BEHÖRIGHET.EKONOMI,
      },
      {
        title: 'Kapitaltillskott',
        path: paths.dashboard.ekonomi.kapitaltillskott,
        icon: ICONS.fastighetslån,
        menyNyckel: 'kapitaltillskott',
        allowedRoles: BEHÖRIGHET.EKONOMI,
      },
      {
        // Bara föräldern bär menyNyckel: nyckeln är den som slås av och på per förening, och
        // två poster med samma nyckel gör katalogen tvetydig.
        title: 'Fastighetslån',
        path: paths.dashboard.ekonomi.fastighetslan.root,
        icon: ICONS.fastighetslån,
        menyNyckel: 'fastighetslan',
        allowedRoles: BEHÖRIGHET.EKONOMI,
        children: [
          {
            title: 'Översikt',
            path: paths.dashboard.ekonomi.fastighetslan.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Översikt V2',
            path: paths.dashboard.ekonomi.fastighetslan.v2,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
        ],
      },
      {
        // Föräldern behöver egna allowedRoles: sidebaren filtreras både på trädet och i
        // layout.tsx, och en ogated förälder läcker att ytan finns.
        title: 'Budget & prognos',
        path: paths.dashboard.ekonomi.budget.root,
        icon: ICONS.ekonomi,
        menyNyckel: 'budget',
        allowedRoles: BEHÖRIGHET.EKONOMI,
      },
    ],
  },

  /**
   * Revision — revisorns egen yta.
   *
   * Sektionen är öppen enbart för behörighetsgruppen `revisor`, som ingen annan
   * roll ger. Föräldern har egna `allowedRoles` och är därmed en hård grind:
   * inget barn kan öppna den för styrelsen av misstag. Ordningen på posterna
   * följer revisionens arbetsordning — väsentlighet och räkenskaper först,
   * uttalandena sist.
   */
  /**
   * Offentlighetsprincipen.
   *
   * Angår enskilda huvudmän i skolväsendet — i Flinky i praktiken föräldrakooperativa
   * förskolor — och är av som standard för alla andra. `standardDold` gör att sektionen måste
   * slås på i organisationens menyinställningar, vilket sker när verksamhetsinriktningen sätts
   * till föräldrakooperativ förskola: onboardingen skriver nyckeln ur `onboarding-floden.ts`,
   * och backend lägger samtidigt upp offentlighetsinställningarna så att handlingsfångstjobbet
   * hittar föreningen. Trädet självt har ingen typlogik, och ska inte få någon.
   *
   * Styrelseledamot och admin får se registret. Att pröva en begäran och avgöra den är
   * snävare och kontrolleras i backend — frontend döljer, backend nekar.
   */
  {
    subheader: 'Offentlighetsprincipen',
    items: [
      {
        title: 'Offentlighetsprincipen',
        path: paths.dashboard.offentlighet.root,
        icon: ICONS.offentlighet,
        menyNyckel: 'offentlighet',
        standardDold: true,
        allowedRoles: BEHÖRIGHET.STYRELSE,
        children: [
          {
            title: 'Allmänna handlingar',
            path: paths.dashboard.offentlighet.handlingar,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Begäran om utlämnande',
            path: paths.dashboard.offentlighet.begäran,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Beskrivning av handlingar',
            path: paths.dashboard.offentlighet.beskrivning,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Inställningar',
            path: paths.dashboard.offentlighet.inställningar,
            allowedRoles: BEHÖRIGHET.ADMIN,
          },
        ],
      },
    ],
  },

  {
    subheader: 'Revision',
    items: [
      {
        title: 'Revision',
        path: paths.dashboard.revision.root,
        icon: ICONS.revision,
        menyNyckel: 'revision',
        allowedRoles: BEHÖRIGHET.REVISOR,
        children: [
          {
            title: 'Översikt',
            path: paths.dashboard.revision.root,
            allowedRoles: BEHÖRIGHET.REVISOR,
          },
          {
            title: 'Räkenskaper',
            path: paths.dashboard.revision.räkenskaper,
            allowedRoles: BEHÖRIGHET.REVISOR,
          },
          {
            title: 'Avstämningar',
            path: paths.dashboard.revision.avstämningar,
            allowedRoles: BEHÖRIGHET.REVISOR,
          },
          {
            title: 'Intern kontroll',
            path: paths.dashboard.revision.internkontroll,
            allowedRoles: BEHÖRIGHET.REVISOR,
          },
          {
            title: 'Närstående & jäv',
            path: paths.dashboard.revision.närstående,
            allowedRoles: BEHÖRIGHET.REVISOR,
          },
          {
            title: 'Analys',
            path: paths.dashboard.revision.analys,
            allowedRoles: BEHÖRIGHET.REVISOR,
          },
          {
            title: 'Förvaltning',
            path: paths.dashboard.revision.förvaltning,
            allowedRoles: BEHÖRIGHET.REVISOR,
          },
          {
            title: 'Rapporter',
            path: paths.dashboard.revision.rapporter,
            allowedRoles: BEHÖRIGHET.REVISOR,
          },
        ],
      },
    ],
  },

  /**
   * Arborist — egen yrkesroll, inget en föreningsmedlem ska möta.
   */
  {
    subheader: 'Arborist',
    items: [
      {
        title: 'Trädvård',
        path: paths.dashboard.arborist.tradvardsplaner,
        icon: ICONS.arborist,
        menyNyckel: 'arborist',
        // Yrkesyta som bara angår enstaka föreningar — av som standard, slås på
        // i organisationens menyinställningar.
        standardDold: true,
        allowedRoles: BEHÖRIGHET.ARBORIST,
        children: [
          {
            title: 'Trädvårdsplaner',
            path: paths.dashboard.arborist.tradvardsplaner,
            allowedRoles: BEHÖRIGHET.ARBORIST,
          },
          {
            title: 'Arborist-pool',
            path: paths.dashboard.arboristPool.root,
            allowedRoles: BEHÖRIGHET.ARBORIST,
          },
          {
            title: 'Skapa offert',
            path: paths.dashboard.offert.trädgårdsoffert,
            allowedRoles: BEHÖRIGHET.ARBORIST,
          },
        ],
      },
    ],
  },

  /**
   * Juridik — avtal och juridiska dokument.
   */
  {
    subheader: 'Juridik',
    items: [
      {
        title: 'PUB-avtal',
        path: paths.dashboard.juridik.pubAvtal,
        icon: ICONS.juridik,
        menyNyckel: 'juridik-pub-avtal',
        allowedRoles: [...BEHÖRIGHET.STYRELSE, ...BEHÖRIGHET.EKONOMI, ...BEHÖRIGHET.KONSULT, ...BEHÖRIGHET.ADMIN],
      },
      {
        title: 'Hyreskontrakt',
        path: paths.dashboard.juridik.hyreskontrakt,
        icon: ICONS.juridik,
        menyNyckel: 'juridik-hyreskontrakt',
        allowedRoles: [...BEHÖRIGHET.BOENDE, ...BEHÖRIGHET.STYRELSE, ...BEHÖRIGHET.EKONOMI, ...BEHÖRIGHET.KONSULT, ...BEHÖRIGHET.ADMIN],
      },
      {
        title: 'Bodelningsavtal',
        path: paths.dashboard.juridik.bodelning,
        icon: ICONS.juridik,
        menyNyckel: 'juridik-bodelning',
        allowedRoles: [...BEHÖRIGHET.BOENDE, ...BEHÖRIGHET.STYRELSE, ...BEHÖRIGHET.EKONOMI, ...BEHÖRIGHET.KONSULT, ...BEHÖRIGHET.ADMIN],
      },
      {
        title: 'Överlåtelseavtal',
        path: paths.dashboard.juridik.overlatelse,
        icon: ICONS.juridik,
        menyNyckel: 'juridik-overlatelse',
        allowedRoles: [...BEHÖRIGHET.BOENDE, ...BEHÖRIGHET.STYRELSE, ...BEHÖRIGHET.EKONOMI, ...BEHÖRIGHET.KONSULT, ...BEHÖRIGHET.ADMIN],
      },
      {
        title: 'Pantförteckning',
        path: paths.dashboard.juridik.pantforteckning,
        icon: ICONS.juridik,
        menyNyckel: 'juridik-pantforteckning',
        allowedRoles: [...BEHÖRIGHET.STYRELSE, ...BEHÖRIGHET.ADMIN],
      },
    ],
  },

  /**
   * Administration — hårda grindar. Föräldrarna har egna `allowedRoles` så att
   * inget barn kan öppna dem av misstag.
   */
  {
    subheader: 'Administration',
    items: [
      {
        title: 'Inställningar',
        path: paths.dashboard.organisation.installningar,
        icon: ICONS.inställningar,
        menyNyckel: 'installningar',
        allowedRoles: BEHÖRIGHET.ADMIN,
        children: [
          {
            title: 'Organisationsinställningar',
            path: paths.dashboard.organisation.installningar,
            allowedRoles: BEHÖRIGHET.ADMIN,
          },
          {
            title: 'Backup',
            path: paths.dashboard.backup.root,
            allowedRoles: BEHÖRIGHET.ADMIN,
          },
        ],
      },
      {
        title: 'System',
        path: paths.dashboard.admin.roller.root,
        icon: ICONS.system,
        allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
        children: [
          {
            title: 'Roller',
            path: paths.dashboard.admin.roller.root,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            // Rolldefinitionerna ovan beskriver vilka roller som finns; den här sätter
            // dem på faktiska användare, i vilken organisation som helst.
            title: 'Användarroller',
            path: paths.dashboard.superadmin.anvandarroller,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            title: 'Förhandsskapade föreningar',
            path: paths.dashboard.superadmin.organisationer,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            title: 'Databas',
            path: paths.dashboard.admin.database,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            title: 'Bokförings-admin',
            path: paths.dashboard.ekonomi.bokforing.admin.root,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            title: 'Mock-data',
            path: paths.dashboard.superadmin.mock,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            title: 'Fellogg',
            path: paths.dashboard.superadmin.fel,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            title: 'Rate limiting',
            path: paths.dashboard.superadmin.rateLimiting,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
        ],
      },
    ],
  },
];

/**
 * Trädet med härledda föräldrabehörigheter. Det är detta som faktiskt filtreras.
 */
export const sidebarsFörBrfAppOchKonsultApp: NavSectionProps['data'] = NAVIGERING.map((grupp) => ({
  ...grupp,
  items: härledBehörigheter(grupp.items),
}));

/**
 * Katalogen över de toppnivåmenyer som kan slås av/på per organisation.
 * Härleds ur trädet ovan — sätt en `menyNyckel` på en toppnivåpost så dyker den
 * upp i organisationens inställningsvy automatiskt.
 */
export const MENY_KATALOG = byggMenyKatalog(sidebarsFörBrfAppOchKonsultApp);

export const MENY_TRÄD = byggMenyTräd(sidebarsFörBrfAppOchKonsultApp);

export function hämtaSidebarsFörAnvändare(
  anvandare: IAnvändare | undefined,
  menySynlighet?: Record<string, boolean>
): NavGroupProps[] {
  return filtreraNavigering(sidebarsFörBrfAppOchKonsultApp, anvandare, menySynlighet);
}

/**
 * Tar bort momsposterna för en organisation som inte är momsregistrerad.
 *
 * Egen funktion i stället för en `menyNyckel` på posterna: nyckeln hade lagt moms i
 * organisationens menyinställningar, och då kunde någon slå på en meny som backend ändå nekar
 * (`EjMomsregistreradException`). Momsregistreringen är en uppgift om föreningen, inte en
 * smaksak om menyer.
 *
 * `Skatt` blir inte tom — Skattekontot ligger kvar under den.
 */
export function utanMomsposter(data: NavGroupProps[]): NavGroupProps[] {
  const ärMomspost = (path?: string) =>
    typeof path === 'string' && path.startsWith(paths.dashboard.ekonomi.moms.root);

  const rensaPost = (item: NavGroupProps['items'][number]): NavGroupProps['items'][number] | null => {
    if (ärMomspost(item.path)) {
      return null;
    }
    if (!item.children) {
      return item;
    }
    const barn = item.children.filter((barnpost) => !ärMomspost(barnpost.path));
    if (barn.length === 0) {
      // Bara momsposter kvar: behåll föräldern som ren länk om den har en egen väg, annars bort.
      if (!item.path) {
        return null;
      }
      const { children, ...utanBarn } = item;
      return utanBarn;
    }
    return { ...item, children: barn };
  };

  return data
    .map((grupp) => ({
      ...grupp,
      items: grupp.items
        .map(rensaPost)
        .filter((item): item is NavGroupProps['items'][number] => item !== null),
    }))
    .filter((grupp) => grupp.items.length > 0);
}
